import React, { useEffect, useState } from "react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import { Card, CardBody, Col, Row, Button, UncontrolledTooltip, Badge } from "reactstrap";
import "../../assets/scss/style.css";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Modal, Form } from "react-bootstrap";
import Pagination, {
  bootstrap5PaginationPreset,
} from "react-responsive-pagination";
import {
  getAllPropertyFeatures,
  getPropertyFeaturesGrouped,
  getPropertyFeatureCategories,
  createPropertyFeature,
  updatePropertyFeature,
  softDeletePropertyFeature,
  hardDeletePropertyFeature,
} from "services/services";
import { successMsgHandler } from "common/successMsgHandler";
import { errorMsgHandler } from "common/errorMsgHandler";
import Swal from "sweetalert2";
import { FaEdit, FaTrash, FaPlus, FaEye, FaEyeSlash } from "react-icons/fa";

const PropertyFeatures = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState([]);
  const [groupedFeatures, setGroupedFeatures] = useState({});
  const [categories, setCategories] = useState([]);
  const [show, setShow] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grouped"); // "grouped" or "list"
  const [activeFilter, setActiveFilter] = useState(""); // "", "true", "false"
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const perPage = 10;

  // Category mapping from display names to API values
  const categoryMapping = {
    "APPLIANCE": "appliances",
    "BASEMENT": "basement",
    "FLOOR COVERING": "flooring",
    "ADDITIONAL FEATURES": "other_amenities",
    "ROOMS": "rooms",
    "ARCHITECTURAL STYLE": "architectural_style",
    "EXTERIOR": "exterior",
    "OUTDOOR AMENITIES": "outdoor_amenities",
    "PARKING": "parking",
    "ROOF": "roof",
    "SURROUNDING": "surrounding",
    "COOLING TYPE": "cooling",
    "HEATING TYPE": "heating",
  };

  const reverseCategoryMapping = Object.fromEntries(
    Object.entries(categoryMapping).map(([key, value]) => [value, key])
  );

  const handleClose = () => {
    setShow(false);
    setIsEdit(false);
    setEditId("");
    formik.resetForm();
  };

  const validationSchema = Yup.object().shape({
    category: Yup.string().required("Category is required"),
    type: Yup.string().required("Type is required"),
    description: Yup.string().required("Description is required"),
    displayOrder: Yup.number()
      .required("Display order is required")
      .min(0, "Display order must be greater than or equal to 0")
      .integer("Display order must be a whole number"),
    isActive: Yup.boolean().required("Active status is required"),
    icon: Yup.string().url("Icon must be a valid URL").nullable(),
  });

  const formik = useFormik({
    initialValues: {
      category: "",
      type: "",
      description: "",
      displayOrder: 0,
      isActive: true,
      icon: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = {
          category: categoryMapping[values.category] || values.category.toLowerCase(),
          type: values.type.toLowerCase().replace(/\s+/g, "_"),
          description: values.description,
          displayOrder: parseInt(values.displayOrder, 10),
          isActive: values.isActive,
        };
        
        if (values.icon) {
          payload.icon = values.icon;
        }

        let res;
        if (isEdit && editId) {
          res = await updatePropertyFeature(editId, payload);
        } else {
          res = await createPropertyFeature(payload);
        }

        const status = res?.code || res?.status || res?.data?.code || res?.data?.status;
        const message = res?.message || res?.data?.message || 
                       (isEdit
                         ? "Property feature updated successfully"
                         : "Property feature created successfully");

        if (status === 200 || status === 201) {
          toast.success(successMsgHandler(message));
          handleClose();
          fetchFeatures();
        } else {
          const errorMsg = res?.data?.message || res?.message || "Something went wrong. Please try again.";
          toast.error(errorMsg);
        }
      } catch (error) {
        console.error("Error saving property feature:", error);
        const errorMessage = error?.response?.data?.message || 
                            error?.response?.data?.error || 
                            error?.message || 
                            "Something went wrong. Please try again.";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      let res;
      
      if (viewMode === "grouped") {
        res = await getPropertyFeaturesGrouped(activeFilter, searchTerm);
        // Handle nested response structure
        const groupedData = res?.data?.data || res?.data || res || {};
        setGroupedFeatures(groupedData);
        setFeatures([]);
        setTotalCount(0); // Grouped view doesn't use pagination
      } else {
        res = await getAllPropertyFeatures(selectedCategory, activeFilter, searchTerm, currentPage, perPage);
        // Handle nested response structure
        let featuresList = res?.data?.data || res?.data?.items || res?.data || res || [];
        
        // Ensure it's an array
        featuresList = Array.isArray(featuresList) ? featuresList : [];
        
        // Get total count from API response or use list length
        const total = res?.data?.total || res?.data?.count || res?.count || featuresList.length;
        
        // If API returns all items, implement client-side pagination
        // Check if we got more items than perPage - if so, the API might not be paginating
        if (featuresList.length > perPage && !res?.data?.total && !res?.data?.count) {
          // Client-side pagination: slice the array
          const startIndex = (currentPage - 1) * perPage;
          const endIndex = startIndex + perPage;
          const paginatedList = featuresList.slice(startIndex, endIndex);
          setFeatures(paginatedList);
          setTotalCount(featuresList.length);
        } else {
          // Server-side pagination or exact count
          setFeatures(featuresList);
          setTotalCount(Number(total) || featuresList.length);
        }
        
        setGroupedFeatures({});
      }
    } catch (error) {
      console.error("Error fetching property features:", error);
      toast.error(errorMsgHandler(error));
      setFeatures([]);
      setGroupedFeatures({});
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await getPropertyFeatureCategories();
      const categoriesList = res?.data?.data || res?.data || res || [];
      setCategories(Array.isArray(categoriesList) ? categoriesList : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      // Use default categories if API fails
      setCategories(Object.keys(categoryMapping));
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [selectedCategory, searchTerm, viewMode, activeFilter]);

  useEffect(() => {
    fetchFeatures();
  }, [selectedCategory, searchTerm, viewMode, activeFilter, currentPage]);

  const handleEdit = (feature) => {
    setIsEdit(true);
    setEditId(feature._id || feature.id);
    formik.setValues({
      category: reverseCategoryMapping[feature.category] || feature.category.toUpperCase(),
      type: feature.type,
      description: feature.description,
      displayOrder: feature.displayOrder || 0,
      isActive: feature.isActive !== undefined ? feature.isActive : true,
      icon: feature.icon || "",
    });
    setShow(true);
  };

  const handleDelete = async (feature, permanent = false) => {
    const result = await Swal.fire({
      title: permanent ? "Permanently delete?" : "Delete Property Feature?",
      text: permanent 
        ? "This action cannot be undone. The feature will be permanently removed."
        : "The feature will be deactivated but can be restored later.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: permanent ? "Yes, delete permanently" : "Yes, deactivate",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        let res;
        if (permanent) {
          res = await hardDeletePropertyFeature(feature._id || feature.id);
        } else {
          res = await softDeletePropertyFeature(feature._id || feature.id);
        }

        const status = res?.code || res?.status || res?.data?.code || res?.data?.status;
        const message = res?.message || res?.data?.message || 
                       (permanent 
                         ? "Property feature permanently deleted"
                         : "Property feature deactivated");

        if (status === 200 || status === 204) {
          toast.success(successMsgHandler(message));
          fetchFeatures();
        } else {
          const errorMsg = res?.data?.message || res?.message || "Something went wrong. Please try again.";
          toast.error(errorMsg);
        }
      } catch (error) {
        console.error("Error deleting property feature:", error);
        const errorMessage = error?.response?.data?.message || 
                            error?.response?.data?.error || 
                            error?.message || 
                            "Something went wrong. Please try again.";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleFeatureStatus = async (feature) => {
    try {
      setLoading(true);
      const res = await updatePropertyFeature(feature._id || feature.id, {
        isActive: !feature.isActive,
      });

      const status = res?.code || res?.status || res?.data?.code || res?.data?.status;
      if (status === 200) {
        toast.success(successMsgHandler("Feature status updated successfully"));
        fetchFeatures();
      } else {
        const errorMsg = res?.data?.message || res?.message || "Something went wrong.";
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Error toggling feature status:", error);
      toast.error(errorMsgHandler(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleBulkImport = async (category, items, categoryName) => {
    const result = await Swal.fire({
      title: `Bulk Import ${categoryName}?`,
      text: `This will add ${items.length} ${categoryName.toLowerCase()} to the database. Continue?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, import all",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        let successCount = 0;
        let errorCount = 0;
        
        // Import items one by one
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          try {
            const payload = {
              category: category,
              type: item.type,
              description: item.description,
              isActive: true,
              displayOrder: item.displayOrder !== undefined ? item.displayOrder : i,
            };

            const res = await createPropertyFeature(payload);
            const status = res?.code || res?.status || res?.data?.code || res?.data?.status;
            
            if (status === 200 || status === 201) {
              successCount++;
            } else {
              errorCount++;
              console.warn(`Failed to create ${item.description}:`, res?.data?.message || res?.message);
            }
          } catch (error) {
            errorCount++;
            console.error(`Error creating ${item.description}:`, error);
            // Continue with next item even if one fails
          }
        }
        
        if (successCount > 0) {
          toast.success(successMsgHandler(`Successfully imported ${successCount} ${categoryName.toLowerCase()}`));
          fetchFeatures();
        }
        
        if (errorCount > 0) {
          toast.error(`${errorCount} ${categoryName.toLowerCase()} failed to import. Some may already exist.`);
        }
        
        if (successCount === 0 && errorCount > 0) {
          toast.error(`Failed to import ${categoryName.toLowerCase()}. They may already exist.`);
        }
      } catch (error) {
        console.error(`Error importing ${categoryName}:`, error);
        const errorMessage = error?.response?.data?.message || 
                            error?.response?.data?.error || 
                            error?.message || 
                            `Failed to import ${categoryName.toLowerCase()}`;
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkImportParking = () => {
    // Parking options from the screenshot
    const parkingOptions = [
      { type: "carport", description: "Carport", displayOrder: 0 },
      { type: "off_street", description: "Off-Street", displayOrder: 1 },
      { type: "garage_attached", description: "Garage-Attached", displayOrder: 2 },
      { type: "on_street", description: "On-Street", displayOrder: 3 },
      { type: "garage_detached", description: "Garage-Detached", displayOrder: 4 },
      { type: "none", description: "None", displayOrder: 5 },
    ];
    
    handleBulkImport("parking", parkingOptions, "Parking");
  };

  const handleBulkImportAdditionalFeatures = () => {
    // Additional features from the first screenshot
    const additionalFeatures = [
      { type: "generator", description: "Generator", displayOrder: 0 },
      { type: "fire_pit", description: "Fire Pit", displayOrder: 1 },
      { type: "sports_court", description: "Sports Court", displayOrder: 2 },
      { type: "outdoor_fireplace", description: "Outdoor Fireplace", displayOrder: 3 },
    ];
    
    handleBulkImport("other_amenities", additionalFeatures, "Additional Features");
  };


  const handleBulkImportCooling = () => {
    // Cooling types from the screenshot
    const coolingTypes = [
      { type: "central", description: "Central", displayOrder: 0 },
      { type: "evaporative", description: "Evaporative", displayOrder: 1 },
      { type: "geothermal", description: "Geothermal", displayOrder: 2 },
      { type: "refrigeration", description: "Refrigeration", displayOrder: 3 },
      { type: "solar", description: "Solar", displayOrder: 4 },
      { type: "wall", description: "Wall", displayOrder: 5 },
      { type: "ac", description: "AC", displayOrder: 6 },
      { type: "other", description: "Other", displayOrder: 7 },
      { type: "none", description: "None", displayOrder: 8 },
    ];
    
    handleBulkImport("cooling", coolingTypes, "Cooling Types");
  };

  const handleBulkImportRoof = () => {
    // Roof types from the screenshot
    const roofTypes = [
      { type: "asphalt", description: "Asphalt", displayOrder: 0 },
      { type: "shake_shingle", description: "Shake / Shingle", displayOrder: 1 },
      { type: "built_up", description: "Built-up", displayOrder: 2 },
      { type: "slate", description: "Slate", displayOrder: 3 },
      { type: "composition", description: "Composition", displayOrder: 4 },
      { type: "tile", description: "Tile", displayOrder: 5 },
      { type: "metal", description: "Metal", displayOrder: 6 },
      { type: "other", description: "Other", displayOrder: 7 },
    ];
    
    handleBulkImport("roof", roofTypes, "Roof Types");
  };

  const handleBulkImportSurrounding = () => {
    // Surrounding types from the screenshot
    const surroundingTypes = [
      { type: "city", description: "City", displayOrder: 0 },
      { type: "mountain", description: "Mountain", displayOrder: 1 },
      { type: "park", description: "Park", displayOrder: 2 },
      { type: "territorial", description: "Territorial", displayOrder: 3 },
      { type: "water", description: "Water", displayOrder: 4 },
      { type: "none", description: "None", displayOrder: 5 },
    ];
    
    handleBulkImport("surrounding", surroundingTypes, "Surrounding Types");
  };

  const handleBulkImportHeating = () => {
    // Heating types from the screenshot
    const heatingTypes = [
      { type: "baseboard", description: "Baseboard", displayOrder: 0 },
      { type: "forced_air", description: "Forced air", displayOrder: 1 },
      { type: "geothermal", description: "Geothermal", displayOrder: 2 },
      { type: "heat_pump", description: "Heat pump", displayOrder: 3 },
      { type: "radiant", description: "Radiant", displayOrder: 4 },
      { type: "stove", description: "Stove", displayOrder: 5 },
      { type: "wall", description: "Wall", displayOrder: 6 },
      { type: "heating_ac", description: "Heating/AC", displayOrder: 7 },
      { type: "other", description: "Other", displayOrder: 8 },
    ];
    
    handleBulkImport("heating", heatingTypes, "Heating Types");
  };

  const handleBulkImportAppliances = () => {
    // Appliances from the screenshot
    const appliances = [
      { type: "dishwasher", description: "Dishwasher", displayOrder: 0 },
      { type: "dryer", description: "Dryer", displayOrder: 1 },
      { type: "garbage_disposal", description: "Garbage Disposal", displayOrder: 2 },
      { type: "freezer", description: "Freezer", displayOrder: 3 },
      { type: "microwave", description: "Microwave", displayOrder: 4 },
      { type: "range_oven", description: "Range / Oven", displayOrder: 5 },
      { type: "refrigerator", description: "Refrigerator", displayOrder: 6 },
      { type: "trash_compactor", description: "Trash Compactor", displayOrder: 7 },
      { type: "washer", description: "Washer", displayOrder: 8 },
    ];
    
    handleBulkImport("appliances", appliances, "Appliances");
  };

  const handleBulkImportBasement = () => {
    // Basement types from the screenshot
    const basementTypes = [
      { type: "finished", description: "Finished", displayOrder: 0 },
      { type: "partially_finished", description: "Partially finished", displayOrder: 1 },
      { type: "unfinished", description: "Unfinished", displayOrder: 2 },
      { type: "none", description: "None", displayOrder: 3 },
    ];
    
    handleBulkImport("basement", basementTypes, "Basement");
  };

  const handleBulkImportFloorCovering = () => {
    // Floor covering types from the screenshot
    const floorCoveringTypes = [
      { type: "carpet", description: "Carpet", displayOrder: 0 },
      { type: "concrete", description: "Concrete", displayOrder: 1 },
      { type: "hardwood_flooring", description: "Hardwood Flooring", displayOrder: 2 },
      { type: "softwood", description: "Softwood", displayOrder: 3 },
      { type: "laminate", description: "Laminate", displayOrder: 4 },
      { type: "linoleum_vinyl", description: "Linoleum / Vinyl", displayOrder: 5 },
      { type: "slate", description: "Slate", displayOrder: 6 },
      { type: "tile", description: "Tile", displayOrder: 7 },
      { type: "other", description: "Other", displayOrder: 8 },
    ];
    
    handleBulkImport("flooring", floorCoveringTypes, "Floor Covering");
  };

  const handleBulkImportRoomDetailsAdditionalFeatures = () => {
    // Additional features from the room details screenshot
    const additionalFeatures = [
      { type: "walk_in_shower", description: "Walk-in Shower", displayOrder: 0 },
      { type: "security_system", description: "Security System", displayOrder: 1 },
      { type: "cable_ready", description: "Cable Ready", displayOrder: 2 },
      { type: "smart_home_controls", description: "Smart Home Controls", displayOrder: 3 },
      { type: "ceiling_fans", description: "Ceiling Fans", displayOrder: 4 },
      { type: "skylights", description: "Skylights", displayOrder: 5 },
      { type: "double_pane_storm_windows", description: "Double Pane/Storm Windows", displayOrder: 6 },
      { type: "vaulted_ceiling", description: "Vaulted Ceiling", displayOrder: 7 },
      { type: "fireplace", description: "Fireplace", displayOrder: 8 },
      { type: "wet_bar", description: "Wet Bar", displayOrder: 9 },
      { type: "wired", description: "Wired", displayOrder: 10 },
      { type: "intercom_system", description: "Intercom System", displayOrder: 11 },
      { type: "jetted_tub", description: "Jetted Tub", displayOrder: 12 },
      { type: "wine_cellar", description: "Wine Cellar", displayOrder: 13 },
      { type: "water_softener_system", description: "Water Softener System", displayOrder: 14 },
    ];
    
    handleBulkImport("other_amenities", additionalFeatures, "Additional Features");
  };

  const handleBulkImportRooms = () => {
    // Rooms from the screenshot
    const rooms = [
      { type: "dining", description: "Dining", displayOrder: 0 },
      { type: "primary_bath", description: "Primary Bath", displayOrder: 1 },
      { type: "workout_room", description: "Workout Room", displayOrder: 2 },
      { type: "attic", description: "Attic", displayOrder: 3 },
      { type: "office", description: "Office", displayOrder: 4 },
      { type: "family_room", description: "Family Room", displayOrder: 5 },
      { type: "laundry_room", description: "Laundry Room", displayOrder: 6 },
      { type: "media_room", description: "Media Room", displayOrder: 7 },
      { type: "sun_room", description: "Sun Room", displayOrder: 8 },
      { type: "multigenerational", description: "Multigenerational", displayOrder: 9 },
      { type: "sauna", description: "Sauna", displayOrder: 10 },
      { type: "mud_room", description: "Mud Room", displayOrder: 11 },
      { type: "guest_room", description: "Guest Room", displayOrder: 12 },
      { type: "pantry", description: "Pantry", displayOrder: 13 },
      { type: "recreational_room", description: "Recreational Room", displayOrder: 14 },
      { type: "library", description: "Library", displayOrder: 15 },
      { type: "walk_in_closet", description: "Walk-in Closet", displayOrder: 16 },
      { type: "primary_suite", description: "Primary Suite", displayOrder: 17 },
    ];
    
    handleBulkImport("rooms", rooms, "Rooms");
  };

  const handleBulkImportArchitecturalStyle = () => {
    // Architectural styles from the screenshot
    const architecturalStyles = [
      { type: "bungalow", description: "Bungalow", displayOrder: 0 },
      { type: "cape_cod", description: "Cape Cod", displayOrder: 1 },
      { type: "colonial", description: "Colonial", displayOrder: 2 },
      { type: "contemporary", description: "Contemporary", displayOrder: 3 },
      { type: "craftsman", description: "Craftsman", displayOrder: 4 },
      { type: "french", description: "French", displayOrder: 5 },
      { type: "georgian", description: "Georgian", displayOrder: 6 },
      { type: "loft", description: "Loft", displayOrder: 7 },
      { type: "modern", description: "Modern", displayOrder: 8 },
      { type: "victorian", description: "Victorian", displayOrder: 9 },
      { type: "santa_fe", description: "Santa fe", displayOrder: 10 },
      { type: "spanish", description: "Spanish", displayOrder: 11 },
      { type: "split_level", description: "Split-level", displayOrder: 12 },
      { type: "tudor", description: "Tudor", displayOrder: 13 },
      { type: "ranch", description: "Ranch", displayOrder: 14 },
      { type: "other", description: "Other", displayOrder: 15 },
    ];
    
    handleBulkImport("architectural_style", architecturalStyles, "Architectural Style");
  };

  const handleBulkImportExterior = () => {
    // Exterior types from the screenshot
    const exteriorTypes = [
      { type: "brick", description: "Brick", displayOrder: 0 },
      { type: "stucco", description: "Stucco", displayOrder: 1 },
      { type: "cement_concrete", description: "Cement / Concrete", displayOrder: 2 },
      { type: "vinyl", description: "Vinyl", displayOrder: 3 },
      { type: "composition", description: "Composition", displayOrder: 4 },
      { type: "wood", description: "Wood", displayOrder: 5 },
      { type: "metal", description: "Metal", displayOrder: 6 },
      { type: "shingle", description: "Shingle", displayOrder: 7 },
      { type: "wood_products", description: "Wood Products", displayOrder: 8 },
      { type: "stone", description: "Stone", displayOrder: 9 },
      { type: "other", description: "Other", displayOrder: 10 },
    ];
    
    handleBulkImport("exterior", exteriorTypes, "Exterior");
  };

  const handleBulkImportOutdoorAmenities = () => {
    // Outdoor amenities from the screenshot
    const outdoorAmenities = [
      { type: "balcony", description: "Balcony", displayOrder: 0 },
      { type: "lawn", description: "Lawn", displayOrder: 1 },
      { type: "bbq_area", description: "BBQ Area", displayOrder: 2 },
      { type: "pond", description: "Pond", displayOrder: 3 },
      { type: "deck", description: "Deck", displayOrder: 4 },
      { type: "pool", description: "Pool", displayOrder: 5 },
      { type: "hot_tub", description: "Hot Tub", displayOrder: 6 },
      { type: "spa", description: "Spa", displayOrder: 7 },
      { type: "dock", description: "Dock", displayOrder: 8 },
      { type: "porch_patio", description: "Porch/Patio", displayOrder: 9 },
      { type: "fenced_yard", description: "Fenced Yard", displayOrder: 10 },
      { type: "rv_parking", description: "RV Parking", displayOrder: 11 },
      { type: "garden", description: "Garden", displayOrder: 12 },
      { type: "greenhouse", description: "Greenhouse", displayOrder: 13 },
      { type: "community_amenities", description: "Community Amenities", displayOrder: 14 },
      { type: "outdoor_kitchen", description: "Outdoor Kitchen", displayOrder: 15 },
      { type: "additional_living_quarters", description: "Additional Living Quarters", displayOrder: 16 },
      { type: "water_view_waterfront", description: "Water View / Waterfront", displayOrder: 17 },
      { type: "storm_shelter", description: "Storm Shelter", displayOrder: 18 },
      { type: "workshop_shed_barn", description: "Workshop/Shed/Barn", displayOrder: 19 },
      { type: "acreage", description: "Acreage", displayOrder: 20 },
      { type: "solar_panels", description: "Solar Panels", displayOrder: 21 },
    ];
    
    handleBulkImport("outdoor_amenities", outdoorAmenities, "Outdoor Amenities");
  };

  const renderGroupedView = () => {
    if (Object.keys(groupedFeatures).length === 0) {
      return (
        <Tr>
          <Td colSpan="5" className="text-center py-4">
            <p className="text-muted">No property features found</p>
          </Td>
        </Tr>
      );
    }

    return Object.entries(groupedFeatures).map(([category, featuresList]) => {
      const displayCategory = reverseCategoryMapping[category] || category.toUpperCase();
      return (
        <React.Fragment key={category}>
          <Tr className="bg-light">
            <Td colSpan="5" className="fw-bold py-2">
              <h6 className="mb-0">{displayCategory}</h6>
            </Td>
          </Tr>
          {Array.isArray(featuresList) && featuresList.length > 0 ? (
            featuresList.map((feature) => (
              <Tr key={feature._id || feature.id}>
                <Td>{feature.description || feature.type}</Td>
                <Td>{feature.type}</Td>
                <Td>{feature.displayOrder || 0}</Td>
                <Td>
                  <Badge color={feature.isActive ? "success" : "secondary"}>
                    {feature.isActive ? "Active" : "Inactive"}
                  </Badge>
                </Td>
                <Td>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      color="info"
                      onClick={() => toggleFeatureStatus(feature)}
                      id={`toggle-${feature._id || feature.id}`}
                    >
                      {feature.isActive ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                    <UncontrolledTooltip target={`toggle-${feature._id || feature.id}`}>
                      {feature.isActive ? "Deactivate" : "Activate"}
                    </UncontrolledTooltip>
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => handleEdit(feature)}
                      id={`edit-${feature._id || feature.id}`}
                    >
                      <FaEdit />
                    </Button>
                    <UncontrolledTooltip target={`edit-${feature._id || feature.id}`}>
                      Edit
                    </UncontrolledTooltip>
                    <Button
                      size="sm"
                      color="danger"
                      onClick={() => handleDelete(feature, false)}
                      id={`delete-${feature._id || feature.id}`}
                    >
                      <FaTrash />
                    </Button>
                    <UncontrolledTooltip target={`delete-${feature._id || feature.id}`}>
                      Delete
                    </UncontrolledTooltip>
                  </div>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan="5" className="text-center py-2 text-muted">
                No features in this category
              </Td>
            </Tr>
          )}
        </React.Fragment>
      );
    });
  };

  const renderListView = () => {
    if (features.length === 0) {
      return (
        <Tr>
          <Td colSpan="6" className="text-center py-4">
            <p className="text-muted">No property features found</p>
          </Td>
        </Tr>
      );
    }

    return features.map((feature) => {
      const displayCategory = reverseCategoryMapping[feature.category] || feature.category.toUpperCase();
      return (
        <Tr key={feature._id || feature.id}>
          <Td>{feature.description || feature.type}</Td>
          <Td>{displayCategory}</Td>
          <Td>{feature.type}</Td>
          <Td>{feature.displayOrder || 0}</Td>
          <Td>
            <Badge color={feature.isActive ? "success" : "secondary"}>
              {feature.isActive ? "Active" : "Inactive"}
            </Badge>
          </Td>
          <Td>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                color="info"
                onClick={() => toggleFeatureStatus(feature)}
                id={`toggle-${feature._id || feature.id}`}
              >
                {feature.isActive ? <FaEyeSlash /> : <FaEye />}
              </Button>
              <UncontrolledTooltip target={`toggle-${feature._id || feature.id}`}>
                {feature.isActive ? "Deactivate" : "Activate"}
              </UncontrolledTooltip>
              <Button
                size="sm"
                color="primary"
                onClick={() => handleEdit(feature)}
                id={`edit-${feature._id || feature.id}`}
              >
                <FaEdit />
              </Button>
              <UncontrolledTooltip target={`edit-${feature._id || feature.id}`}>
                Edit
              </UncontrolledTooltip>
              <Button
                size="sm"
                color="danger"
                onClick={() => handleDelete(feature, false)}
                id={`delete-${feature._id || feature.id}`}
              >
                <FaTrash />
              </Button>
              <UncontrolledTooltip target={`delete-${feature._id || feature.id}`}>
                Delete
              </UncontrolledTooltip>
            </div>
          </Td>
        </Tr>
      );
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Row>
            <Col>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-0">Property Features Management</h4>
                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        onClick={() => {
                          setIsEdit(false);
                          setEditId("");
                          formik.resetForm();
                          setShow(true);
                        }}
                      >
                        <FaPlus className="me-1" />
                        Add Feature
                      </Button>
                    </div>
                  </div>

                  {/* Bulk Import Section for Room Details */}
                  <Row className="mb-3">
                    <Col>
                      <Card className="bg-light">
                        <CardBody>
                          <h6 className="mb-3">Bulk Import Room Details</h6>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportAppliances}
                              disabled={loading}
                            >
                              Import Appliances
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportBasement}
                              disabled={loading}
                            >
                              Import Basement
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportFloorCovering}
                              disabled={loading}
                            >
                              Import Floor Covering
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportRoomDetailsAdditionalFeatures}
                              disabled={loading}
                            >
                              Import Additional Features
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportRooms}
                              disabled={loading}
                            >
                              Import Rooms
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportArchitecturalStyle}
                              disabled={loading}
                            >
                              Import Architectural Style
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportExterior}
                              disabled={loading}
                            >
                              Import Exterior
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportOutdoorAmenities}
                              disabled={loading}
                            >
                              Import Outdoor Amenities
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportParking}
                              disabled={loading}
                            >
                              Import Parking
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportSurrounding}
                              disabled={loading}
                            >
                              Import Surrounding
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportRoof}
                              disabled={loading}
                            >
                              Import Roof
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportCooling}
                              disabled={loading}
                            >
                              Import Cooling Type
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleBulkImportHeating}
                              disabled={loading}
                            >
                              Import Heating Type
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Category</Form.Label>
                        <Form.Select
                          value={selectedCategory}
                          onChange={(e) => {
                            setSelectedCategory(e.target.value);
                            if (viewMode === "grouped") {
                              setViewMode("list");
                            }
                          }}
                        >
                          <option value="">All Categories</option>
                          {Object.keys(categoryMapping).map((cat) => (
                            <option key={cat} value={categoryMapping[cat]}>
                              {cat}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={activeFilter}
                          onChange={(e) => setActiveFilter(e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Search</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Search features..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    
                  </Row>

                  {/* Table */}
                  <div className="table-responsive">
                    <Table className="table table-bordered table-striped">
                      <Thead>
                        <Tr>
                          {viewMode === "grouped" ? (
                            <>
                              <Th>Description</Th>
                              <Th>Type</Th>
                              <Th>Display Order</Th>
                              <Th>Status</Th>
                              <Th>Actions</Th>
                            </>
                          ) : (
                            <>
                              <Th>Description</Th>
                              <Th>Category</Th>
                              <Th>Type</Th>
                              <Th>Display Order</Th>
                              <Th>Status</Th>
                              <Th>Actions</Th>
                            </>
                          )}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {loading ? (
                          <Tr>
                            <Td colSpan={viewMode === "grouped" ? 5 : 6} className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </Td>
                          </Tr>
                        ) : viewMode === "grouped" ? (
                          renderGroupedView()
                        ) : (
                          renderListView()
                        )}
                      </Tbody>
                    </Table>
                  </div>
                  
                  {/* Pagination - Only show for list view */}
                  {viewMode === "list" && totalCount > perPage && (
                    <Row className="mt-3">
                      <Col md={9}></Col>
                      <Col md={3}>
                        <Pagination
                          {...bootstrap5PaginationPreset}
                          current={currentPage}
                          total={Math.ceil(totalCount / perPage)}
                          onPageChange={handlePageChange}
                          className="pagination justify-content-end gap-1"
                        />
                      </Col>
                    </Row>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit Property Feature" : "Add Property Feature"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={formik.handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Category *</Form.Label>
              <Form.Select
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select Category</option>
                {Object.keys(categoryMapping).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Form.Select>
              {formik.touched.category && formik.errors.category && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {formik.errors.category}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type *</Form.Label>
              <Form.Control
                type="text"
                name="type"
                placeholder="e.g., home_office, dishwasher"
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <Form.Text className="text-muted">
                Will be converted to lowercase snake_case automatically
              </Form.Text>
              {formik.touched.type && formik.errors.type && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {formik.errors.type}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                type="text"
                name="description"
                placeholder="e.g., Home Office, Dishwasher"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.description && formik.errors.description && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {formik.errors.description}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Display Order *</Form.Label>
              <Form.Control
                type="number"
                name="displayOrder"
                min="0"
                value={formik.values.displayOrder}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.displayOrder && formik.errors.displayOrder && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {formik.errors.displayOrder}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Icon URL (Optional)</Form.Label>
              <Form.Control
                type="url"
                name="icon"
                placeholder="https://example.com/icons/icon.png"
                value={formik.values.icon}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.icon && formik.errors.icon && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {formik.errors.icon}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                name="isActive"
                label="Active"
                checked={formik.values.isActive}
                onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default PropertyFeatures;

